'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useFullscreenItemId,
  useOverviewMode,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import type { PanelOpenDetail } from '@/components/workspace/panels/panel-events';
import { workspaceIsEmpty } from '@/lib/workspaces/screens';
import { hasDeepLinkSpawnIntent } from '@/lib/workspaces/deep-link-intent';
import { DiscoverView } from './discover-view';
import { prefetchDiscover } from './discover-data';
import {
  DISCOVER_TOGGLE_EVENT,
  closeDiscover,
  openDiscover,
  setDiscoverDocked,
  useDiscoverOpen,
} from './discover-events';

/**
 * The opened Discover mount: full width over the CANVAS AREA, never the
 * viewport — the rail stays visible throughout, so leaving is always one
 * click and this never reads as a takeover (the same distinction that keeps
 * starters on the tiling path instead of the fullscreen one).
 *
 * This host is also the arbiter for the rail press / ⌘⇧K: it has store
 * access, so it is the one place that can tell whether the INLINE mount is
 * currently showing. In that state the press focuses the inline field
 * instead of opening a second Discover over an identical surface.
 *
 * Shrink docks the overlay to a left column beside your work, persisted per
 * device like other layout preferences.
 */
const SHRINK_KEY = 'ws-discover-shrunk';

export function DiscoverHost() {
  const open = useDiscoverOpen();
  // Warm Discover's first-paint payloads once the workspace is up, so the
  // FIRST open renders complete instead of popping bands in as fetches land.
  useEffect(() => {
    prefetchDiscover();
  }, []);
  const items = useWorkspaceItems();
  const overviewMode = useOverviewMode();
  const fullscreenItemId = useFullscreenItemId();

  useEffect(() => {
    const onToggle = () => {
      if (open) closeDiscover();
      else openDiscover();
    };
    window.addEventListener(DISCOVER_TOGGLE_EVENT, onToggle);
    return () => window.removeEventListener(DISCOVER_TOGGLE_EVENT, onToggle);
  }, [open]);

  // An EMPTY workspace opens straight into Discover, full-surface, with the
  // rail button lit - the fullscreen greeting replaced the old inline card
  // (user call). Once per mount, on the SSR-provided item list (reliable at
  // first render): closing it is respected, taking something exits via the
  // spawn autoclose, and an emptied-later workspace does not trap the user
  // back in.
  const openedForEmptyRef = useRef(false);
  useEffect(() => {
    if (openedForEmptyRef.current) return;
    openedForEmptyRef.current = true;
    // A spawn-bearing deep link (?chat=, ?spawn=, ?preview=, ?dialog=)
    // means the visitor arrived to DO something — not a blank first visit.
    // The overlay would only flash open and autoclose on the spawn's focus,
    // and its render + prefetch burst lands exactly in the spawn chain's
    // critical window (the smoke's one-shot open-chat gate). Yield. Both
    // effect orders are covered: the consumer flags the intent BEFORE it
    // strips the URL; if this effect wins the race instead, the params are
    // still on the URL.
    try {
      const params = new URLSearchParams(window.location.search);
      if (
        hasDeepLinkSpawnIntent() ||
        params.has('chat') ||
        params.has('spawn') ||
        params.has('preview') ||
        params.has('dialog') ||
        params.has('discover') ||
        params.has('marketplace')
      ) {
        return;
      }
    } catch {
      /* URL parsing never blocks the empty-workspace greeting */
    }
    if (workspaceIsEmpty(items) && !overviewMode && !fullscreenItemId) {
      openDiscover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ONE ACTIVE GO-PLACES VERB: opening Discover exits Stage. The two rail
  // buttons must never read pressed together — Discover covers the canvas,
  // so a lit Stage underneath is a claim the UI can't honor. Dispatching the
  // overview exit HERE, in an effect defined BEFORE the autoclose effect,
  // means the ws:screen it fires happens while this render's autoclose
  // listener is not yet attached — the fresh open survives. Covers every
  // open path: rail press, ⌘⇧K, deep links, live level pushes.
  const overviewRef = useRef(overviewMode);
  overviewRef.current = overviewMode;
  useEffect(() => {
    if (!open || !overviewRef.current) return;
    window.dispatchEvent(
      new CustomEvent('ws:screen', { detail: { kind: 'overview-toggle' } }),
    );
  }, [open]);

  // AUTOCLOSE: the overlay yields to any navigation-away intent — a screen
  // move (Stage toggle, a taskbar puck, digit chords), a spawn landing
  // anywhere (every add fires ws:focus-item), or a panel opening (Alerts,
  // account, the desktop panel views, which would otherwise open UNDER the
  // z-95 overlay and read as a dead click). "Taking something is the exit."
  //
  // A PANEL CLOSING IS NOT ONE OF THEM (2026-10-03). `closeWorkspacePanel`
  // announces itself on the same event as an open, with `close: true`, and
  // this listener took it for a panel opening. Harmless while nothing closed
  // a panel under an open Discover; then the notifications card learned to
  // close on a press outside itself over a full-screen app (top-panel.tsx),
  // and with that card open underneath, the first press INSIDE Discover
  // closed the card and Discover with it. A close takes nothing, so it is
  // skipped. tests/unit/workspaces/discover-ignores-panel-close.test.tsx
  useEffect(() => {
    if (!open) return;
    const close = () => closeDiscover();
    const onPanel = (e: Event) => {
      if ((e as CustomEvent<PanelOpenDetail | undefined>).detail?.close) return;
      closeDiscover();
    };
    window.addEventListener('ws:screen', close);
    window.addEventListener('ws:focus-item', close);
    window.addEventListener('workspace-panel-open', onPanel);
    return () => {
      window.removeEventListener('ws:screen', close);
      window.removeEventListener('ws:focus-item', close);
      window.removeEventListener('workspace-panel-open', onPanel);
    };
  }, [open]);

  const shrunkRef = useRef<boolean | null>(null);
  if (shrunkRef.current === null && typeof window !== 'undefined') {
    try {
      shrunkRef.current = window.localStorage.getItem(SHRINK_KEY) === '1';
    } catch {
      shrunkRef.current = false;
    }
  }

  if (!open) return null;
  return <DiscoverOverlay initialShrunk={shrunkRef.current ?? false} />;
}

function DiscoverOverlay({ initialShrunk }: { initialShrunk: boolean }) {
  const t = useT();
  const [shrunk, setShrunk] = useState(initialShrunk);
  // Shrunk is a COLUMN beside your work only from md up (the `md:` classes
  // below); under md the overlay is the whole canvas area whatever the
  // preference says. Published so the ad slot knows whether Discover covers
  // the canvas (components/workspace/canvas-cover.ts); every open starts as
  // covering, so the frame before this runs errs on the covered side.
  const isMd = useIsMd();
  useEffect(() => {
    setDiscoverDocked(shrunk && isMd);
  }, [shrunk, isMd]);
  const toggleShrunk = () =>
    setShrunk((s) => {
      const next = !s;
      try {
        window.localStorage.setItem(SHRINK_KEY, next ? '1' : '0');
      } catch {
        // Preference simply does not stick in private mode.
      }
      return next;
    });
  return (
    <div
      data-testid="workspace-discover-overlay"
      className={cn(
        'absolute inset-y-0 left-0 z-[95] flex flex-col border-border/60 bg-background pointer-events-auto',
        // Below md the overlay is ALWAYS the full canvas area — a 26rem
        // column beside your work is a desktop idea, and on a phone it left
        // a dead sliver of canvas peeking out.
        shrunk ? 'inset-x-0 md:inset-x-auto md:w-[26rem] md:max-w-full md:border-r md:shadow-xl' : 'inset-x-0',
      )}
    >
      <div className="flex shrink-0 items-center justify-between px-4 pt-3 md:px-5">
        <span className="text-sm font-semibold text-foreground">Discover</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleShrunk}
            className="hidden h-7 items-center gap-1 rounded-md px-2 text-[11px] text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground md:inline-flex"
          >
            <Minimize2 className="h-3.5 w-3.5" />
            {shrunk ? t('workspace.panels.expand') : t('discoverView.shrink')}
          </button>
          <button
            type="button"
            onClick={closeDiscover}
            aria-label={t('workspace.canvas.node.close')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Padding clears the mobile tab bar on whichever edge it occupies
          (bottom in portrait, LEFT on landscape handsets) - the bar
          publishes its edge as CSS vars and renders ABOVE this overlay so
          switching stays one tap while browsing. */}
      <div
        className="min-h-0 flex-1"
        style={{
          paddingBottom:
            'calc(var(--ws-bar-b, 0px) + env(safe-area-inset-bottom))',
          paddingLeft: 'var(--ws-bar-l, 0px)',
        }}
      >
        <DiscoverView variant="overlay" onClose={closeDiscover} />
      </div>
    </div>
  );
}
